import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import * as d3 from 'd3';
import {
  Search, Shield, Globe, Server, Lock, Eye, Activity, Zap,
  Database, Terminal, AlertTriangle, CheckCircle, XCircle,
  FileText, Network, Target, Clock, ChevronDown, ChevronRight,
  Layers, Code, Hash, MapPin, Calendar, ExternalLink,
  ZoomIn, ZoomOut, Maximize2, Info, Filter, Sliders
} from 'lucide-react';
import { apiRequest } from '../utils/api';

const GlobalStyle = createGlobalStyle`
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { 
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; 
    background: #ffffff; 
    color: #111827; 
    line-height: 1.5; 
    overflow: hidden; 
  }
`;

const scanPulse = keyframes`
  0%, 100% { opacity: 0.8; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.01); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 10px rgba(30, 64, 175, 0.2); }
  50% { box-shadow: 0 0 15px rgba(30, 64, 175, 0.3); }
`;

const PageContainer = styled.div`
  height: 100vh;
  background: #ffffff;
  position: relative;
  overflow: hidden;
`;

const MainWrapper = styled.div`
  position: relative;
  z-index: 1;
  height: 100vh;
  display: flex;
  flex-direction: column;
`;

const SearchSection = styled.div`
  flex-shrink: 0;
  padding: 1.25rem;
  border-bottom: 1px solid #f1f5f9;
  background: white;
  animation: ${slideIn} 0.4s ease-out;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const SearchForm = styled.form`
  display: flex;
  gap: 0.75rem;
  max-width: 100%;
  margin: 0 auto;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.625rem;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  z-index: 2;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 0.75rem 0.75rem 2.5rem;
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 0.5rem;
  color: #111827;
  font-size: 0.875rem;
  font-weight: 400;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
    animation: ${glow} 2s infinite;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
    font-size: 0.875rem;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f9fafb;
  }
`;

const ProgressBarWrapper = styled.div`
  flex: 0 0 180px;
  display: flex;
  align-items: center;
  gap: 0.625rem;

  @media (max-width: 768px) {
    flex: 0 0 auto;
    width: 100%;
  }
`;

const ProgressBarContainer = styled.div`
  flex: 1;
  height: 6px;
  background: #f1f5f9;
  border-radius: 3px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
`;

const ProgressBar = styled.div`
  width: ${props => props.width}%;
  height: 100%;
  background: linear-gradient(90deg, #1e40af 0%, #3b82f6 100%);
  border-radius: 3px;
  transition: width 0.3s ease;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -50%;
    width: 50%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    animation: ${scanPulse} 2s infinite;
  }
`;

const ProgressText = styled.span`
  font-size: 0.75rem;
  color: #1e40af;
  font-weight: 500;
  white-space: nowrap;
`;

const SearchButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(90deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  box-shadow: 0 4px 6px rgba(30, 64, 175, 0.2);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 12px rgba(30, 64, 175, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    animation: ${scanPulse} 2s infinite;
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const ErrorSection = styled.div`
  flex-shrink: 0;
  padding: 0.875rem 1.25rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
  margin: 1rem;
  animation: ${slideIn} 0.3s ease-out;
`;

const ErrorMessage = styled.div`
  color: #991b1b;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  font-size: 0.875rem;
`;

const MainContentLayout = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 280px 1fr 320px;
  gap: 0;
  height: calc(100vh - 120px);
  overflow: hidden;

  @media (max-width: 1200px) {
    grid-template-columns: 250px 1fr 280px;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    grid-template-rows: 200px 1fr 250px;
    height: calc(100vh - 120px);
  }
`;

const ScansListSection = styled.div`
  background: white;
  border-right: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 1px 0 2px rgba(0, 0, 0, 0.05);

  @media (max-width: 1024px) {
    border-right: none;
    border-bottom: 1px solid #f1f5f9;
  }
`;

const ScansHeader = styled.div`
  flex-shrink: 0;
  padding: 1rem;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
`;

const ScansTitle = styled.h2`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const ScansList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 2px;
  }
`;

const ScanItem = styled.div`
  background: ${props => props.active ? 'rgba(30, 64, 175, 0.05)' : 'white'};
  border: 1px solid ${props => props.active ? '#3b82f6' : '#f1f5f9'};
  border-radius: 0.5rem;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    background: rgba(30, 64, 175, 0.03);
    border-color: #3b82f6;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(30, 64, 175, 0.1);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const ScanItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.375rem;
`;

const ScanDomain = styled.div`
  font-weight: 500;
  color: ${props => props.active ? '#1e40af' : '#111827'};
  font-size: 0.8125rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ScanDate = styled.div`
  font-size: 0.6875rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
`;

const ScanId = styled.div`
  font-size: 0.625rem;
  color: #9ca3af;
  font-family: monospace;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VisualizationSection = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  overflow: hidden;
`;

const VisualizationHeader = styled.div`
  flex-shrink: 0;
  padding: 1rem;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const VisualizationHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const VisualizationTitle = styled.h2`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const VisualizationHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const FilterControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 0.375rem 0.625rem;
  background: ${props => props.active ? 'rgba(30, 64, 175, 0.9)' : 'white'};
  border: 1px solid ${props => props.active ? '#3b82f6' : '#f1f5f9'};
  border-radius: 0.375rem;
  color: ${props => props.active ? '#ffffff' : '#374151'};
  font-size: 0.6875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    background: ${props => props.active ? 'rgba(30, 64, 175, 0.8)' : '#f9fafb'};
    border-color: #3b82f6;
    color: ${props => props.active ? '#ffffff' : '#1e40af'};
  }
`;

const NodeCount = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  padding: 0.375rem 0.625rem;
  background: white;
  border-radius: 0.375rem;
  border: 1px solid #f1f5f9;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  font-weight: 500;
`;

const VisualizationContent = styled.div`
  flex: 1;
  padding: 1rem;
  position: relative;
  overflow: hidden;
`;

const VisualizationContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: #f9fafb;
  border-radius: 0.5rem;
  border: 1px solid #f1f5f9;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

const SVGContainer = styled.svg`
  width: 100%;
  height: 100%;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const Controls = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  z-index: 10;
`;

const ControlButton = styled.button`
  width: 32px;
  height: 32px;
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 0.5rem;
  color: #374151;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover {
    background: rgba(30, 64, 175, 0.9);
    border-color: #3b82f6;
    color: white;
    transform: scale(1.05);
  }
`;

const Legend = styled.div`
  position: absolute;
  bottom: 0.75rem;
  left: 0.75rem;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #f1f5f9;
  border-radius: 0.5rem;
  padding: 0.75rem;
  backdrop-filter: blur(10px);
  z-index: 10;
  max-height: 180px;
  overflow-y: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 1.5px;
  }

  &::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 1.5px;
  }
`;

const LegendTitle = styled.h4`
  color: #111827;
  font-size: 0.75rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.375rem;
  font-size: 0.6875rem;
  color: #4b5563;

  &:last-child {
    margin-bottom: 0;
  }
`;

const LegendDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.color};
  border: 1px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
`;

const LegendCount = styled.span`
  color: #9ca3af;
  font-size: 0.625rem;
  margin-left: auto;
  font-weight: 500;
`;

const Tooltip = styled.div`
  position: absolute;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #f1f5f9;
  border-radius: 0.375rem;
  padding: 0.5rem;
  color: #111827;
  font-size: 0.6875rem;
  pointer-events: none;
  z-index: 20;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-width: 160px;
  line-height: 1.3;
`;

const EmptyVisualizationState = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #9ca3af;
  padding: 1.5rem;
`;

const EmptyIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  border: 2px dashed #e5e7eb;
`;

const EmptyTitle = styled.h3`
  font-size: 0.9375rem;
  color: #6b7280;
  margin: 0 0 0.375rem 0;
  font-weight: 500;
`;

const EmptyDescription = styled.p`
  font-size: 0.8125rem;
  color: #9ca3af;
  max-width: 240px;
  margin: 0;
  line-height: 1.4;
`;

const DetailsPanel = styled.div`
  background: white;
  border-left: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: -1px 0 2px rgba(0, 0, 0, 0.05);

  @media (max-width: 1024px) {
    border-left: none;
    border-top: 1px solid #f1f5f9;
  }
`;

const DetailsPanelHeader = styled.div`
  flex-shrink: 0;
  padding: 1rem;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
`;

const DetailsPanelTitle = styled.h2`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const DetailsPanelContent = styled.div`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 2px;
  }
`;

const EmptyDetailsState = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #9ca3af;
  padding: 1.5rem;
`;

const NodeDetails = styled.div`
  animation: ${fadeIn} 0.2s ease-out;
`;

const NodeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f1f5f9;
`;

const NodeIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 0.5rem;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const NodeTitle = styled.div`
  flex: 1;
  min-width: 0;
`;

const NodeName = styled.h3`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.25rem 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NodeType = styled.div`
  font-size: 0.6875rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
`;

const DataSection = styled.div`
  margin-bottom: 1.25rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DataSectionTitle = styled.h4`
  font-size: 0.8125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.625rem 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding-bottom: 0.375rem;
  border-bottom: 2px solid #3b82f6;
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

const DataContainer = styled.div`
  background: #f9fafb;
  border-radius: 0.5rem;
  border: 1px solid #f1f5f9;
  padding: 0.875rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const DataGrid = styled.div`
  display: grid;
  gap: 0.625rem;
`;

const DataRow = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 0.625rem;
  padding: 0.625rem;
  background: white;
  border-radius: 0.375rem;
  border: 1px solid #f1f5f9;
  align-items: start;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.25rem;
  }
`;

const DataLabel = styled.div`
  font-weight: 500;
  color: #4b5563;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DataValue = styled.div`
  color: #111827;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.4;
  word-break: break-word;
`;

const NestedContainer = styled.div`
  background: white;
  border-radius: 0.375rem;
  padding: 0.625rem;
  margin-top: 0.25rem;
  border: 1px solid #f1f5f9;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const ArrayContainer = styled.div`
  background: #f3f4f6;
  border-radius: 0.375rem;
  padding: 0.625rem;
  margin-top: 0.25rem;
  border-left: 2px solid #3b82f6;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const ArrayItem = styled.div`
  padding: 0.25rem 0;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.6875rem;

  &:last-child {
    border-bottom: none;
  }
`;

const KeyValuePair = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 0.625rem;
  padding: 0.25rem 0;
  border-bottom: 1px solid rgba(241, 245, 249, 0.8);
  align-items: start;

  &:last-child {
    border-bottom: none;
  }
`;

const ObjectKey = styled.div`
  font-size: 0.6875rem;
  font-weight: 500;
  color: #4b5563;
  text-transform: capitalize;
`;

const ObjectValue = styled.div`
  font-size: 0.6875rem;
  color: #111827;
  word-break: break-word;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1875rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  ${props => {
    switch (props.variant) {
      case 'success':
        return `
          background: rgba(16, 185, 129, 0.1);
          color: #059669;
          border: 1px solid rgba(16, 185, 129, 0.2);
        `;
      case 'error':
        return `
          background: rgba(239, 68, 68, 0.1);
          color: #dc2626;
          border: 1px solid rgba(239, 68, 68, 0.2);
        `;
      case 'warning':
        return `
          background: rgba(245, 158, 11, 0.1);
          color: #d97706;
          border: 1px solid rgba(245, 158, 11, 0.2);
        `;
      default:
        return `
          background: rgba(107, 114, 128, 0.1);
          color: #4b5563;
          border: 1px solid rgba(107, 114, 128, 0.2);
        `;
    }
  }}
`;

const NODE_COLORS = {
  domain: '#1e40af',
  subdomain: '#06b6d4',
  ip: '#059669',
  ssl: '#d97706',
  tech: '#7c3aed',
  port: '#dc2626',
  dns: '#db2777',
  whois: '#6b7280'
};

const NODE_SIZES = {
  domain: 22,
  subdomain: 16,
  ip: 14,
  ssl: 12,
  tech: 10,
  port: 8,
  dns: 10,
  whois: 12
};

const SCAN_DURATION_MS = 60000;

// Fields to exclude from display
const EXCLUDED_FIELDS = new Set([
  'id',
  'failed_ports',
  'updated_at',
  'created_at',
  'index',
  'scan_id'
]);

export default function ReconnaissancePage() {
  const [domain, setDomain] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [scans, setScans] = useState([]);
  const [loadingScans, setLoadingScans] = useState(false);
  const [activeScanId, setActiveScanId] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [nodeFilter, setNodeFilter] = useState('all');
  const [nodeCounts, setNodeCounts] = useState({});
  const timerRef = useRef(null);
  const svgRef = useRef(null);
  const simulationRef = useRef(null);

  useEffect(() => {
    loadScans();
  }, []);

  const loadScans = async () => {
    setLoadingScans(true);
    try {
      const res = await apiRequest('/api/analysis/scans/');
      if (res.ok) {
        const data = await res.json();
        const sortedScans = (data.scans || []).sort((a, b) => {
          return new Date(b.created_at) - new Date(a.created_at);
        });
        setScans(sortedScans);
        if (sortedScans.length > 0 && !activeScanId) {
          setActiveScanId(sortedScans[0].id);
          handleScanClick(sortedScans[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to load scans:', err);
    } finally {
      setLoadingScans(false);
    }
  };

  const startProgress = () => {
    setProgress(0);
    const startTime = Date.now();

    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      let percent = Math.min((elapsed / SCAN_DURATION_MS) * 100, 100);
      setProgress(percent);
      if (percent >= 100) {
        clearInterval(timerRef.current);
      }
    }, 200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResults(null);
    setSelectedNode(null);

    if (!domain.trim()) {
      setError('Please enter a valid domain.');
      return;
    }

    setIsScanning(true);
    startProgress();

    try {
      const res = await apiRequest('/api/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: domain.trim() }),
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to start scan.');
      }

      setTimeout(async () => {
        setIsScanning(false);
        setProgress(100);
        clearInterval(timerRef.current);
        await loadScans();
      }, SCAN_DURATION_MS + 2000);
    } catch (err) {
      setError(err.message);
      setIsScanning(false);
      clearInterval(timerRef.current);
    }
  };

  const handleScanClick = async (scanId) => {
    setActiveScanId(scanId);
    setSelectedNode(null);
    try {
      const res = await apiRequest(`/api/analysis/scans/${scanId}/`);
      if (res.ok) {
        const data = await res.json();
        setResults(data);
        setError(null);
      } else {
        const errData = await res.json();
        setError(errData.error || 'Failed to load scan results.');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const createNodesAndLinks = (scanData, filter = 'all') => {
    const allNodes = [];
    const allLinks = [];
    const counts = {
      all: 0,
      domain: 0,
      subdomain: 0,
      ip: 0,
      ssl: 0,
      tech: 0,
      port: 0,
      dns: 0,
      whois: 0
    };

    if (!scanData) return { nodes: [], links: [], counts };

    // Create all nodes first
    // Main domain node
    const mainNode = {
      id: scanData.domain,
      type: 'domain',
      label: scanData.domain,
      data: scanData,
      color: NODE_COLORS.domain,
      size: NODE_SIZES.domain
    };
    allNodes.push(mainNode);
    counts.domain = 1;

    // IP Details
    if (scanData.ip_details) {
      const ipNode = {
        id: `ip-${scanData.ip_details.ip || 'unknown'}`,
        type: 'ip',
        label: scanData.ip_details.ip || 'Unknown IP',
        data: scanData.ip_details,
        color: NODE_COLORS.ip,
        size: NODE_SIZES.ip
      };
      allNodes.push(ipNode);
      allLinks.push({ source: scanData.domain, target: ipNode.id, type: 'resolves_to' });
      counts.ip = 1;
    }

    // Subdomains
    if (scanData.subdomains?.subdomains) {
      const subdomains = Array.isArray(scanData.subdomains.subdomains)
        ? scanData.subdomains.subdomains
        : [];

      subdomains.forEach((subdomain, index) => {
        const subdomainNode = {
          id: `subdomain-${subdomain}-${index}`,
          type: 'subdomain',
          label: subdomain,
          data: { subdomain, index },
          color: NODE_COLORS.subdomain,
          size: NODE_SIZES.subdomain
        };
        allNodes.push(subdomainNode);
        allLinks.push({ source: scanData.domain, target: subdomainNode.id, type: 'has_subdomain' });
        counts.subdomain++;
      });
    }

    // SSL Details
    if (scanData.ssl_details) {
      const sslNode = {
        id: `ssl-${scanData.domain}`,
        type: 'ssl',
        label: 'SSL Certificate',
        data: scanData.ssl_details,
        color: NODE_COLORS.ssl,
        size: NODE_SIZES.ssl
      };
      allNodes.push(sslNode);
      allLinks.push({ source: scanData.domain, target: sslNode.id, type: 'has_ssl' });
      counts.ssl = 1;
    }

    // Technology Stack
    if (scanData.tech_stack) {
      Object.entries(scanData.tech_stack).forEach(([url, techData]) => {
        if (techData.tech && techData.tech.length > 0) {
          techData.tech.forEach((tech, index) => {
            const techNode = {
              id: `tech-${tech.name}-${url}-${index}`,
              type: 'tech',
              label: tech.name,
              data: { ...tech, url },
              color: NODE_COLORS.tech,
              size: NODE_SIZES.tech
            };
            allNodes.push(techNode);
            allLinks.push({ source: scanData.domain, target: techNode.id, type: 'uses_tech' });
            counts.tech++;
          });
        }
      });
    }

    // Open Ports (excluding failed ports)
    if (scanData.open_ports?.openPorts) {
      const ports = Array.isArray(scanData.open_ports.openPorts)
        ? scanData.open_ports.openPorts
        : [];

      ports.forEach((port, index) => {
        const portNode = {
          id: `port-${port}-${index}`,
          type: 'port',
          label: `Port ${port}`,
          data: { port, status: 'open', index },
          color: NODE_COLORS.port,
          size: NODE_SIZES.port
        };
        allNodes.push(portNode);
        allLinks.push({ source: scanData.domain, target: portNode.id, type: 'has_open_port' });
        counts.port++;
      });
    }

    // DNS Records
    if (scanData.dig_output?.ips) {
      const ips = Array.isArray(scanData.dig_output.ips)
        ? scanData.dig_output.ips
        : [];

      ips.forEach((ip, index) => {
        const dnsNode = {
          id: `dns-${ip}-${index}`,
          type: 'dns',
          label: ip,
          data: { ip, type: 'A_record', index },
          color: NODE_COLORS.dns,
          size: NODE_SIZES.dns
        };
        allNodes.push(dnsNode);
        allLinks.push({ source: scanData.domain, target: dnsNode.id, type: 'dns_record' });
        counts.dns++;
      });
    }

    // WHOIS Data
    if (scanData.whois_data) {
      const whoisNode = {
        id: `whois-${scanData.domain}`,
        type: 'whois',
        label: 'WHOIS Data',
        data: scanData.whois_data,
        color: NODE_COLORS.whois,
        size: NODE_SIZES.whois
      };
      allNodes.push(whoisNode);
      allLinks.push({ source: scanData.domain, target: whoisNode.id, type: 'has_whois' });
      counts.whois = 1;
    }

    // Now filter nodes based on the filter
    let filteredNodes;
    if (filter === 'all') {
      filteredNodes = allNodes;
    } else {
      filteredNodes = allNodes.filter(node => node.type === filter);
    }

    // Create a Set of filtered node IDs for quick lookup
    const filteredNodeIds = new Set(filteredNodes.map(node => node.id));

    // Filter links to only include those where both source and target exist in filtered nodes
    const filteredLinks = allLinks.filter(link =>
      filteredNodeIds.has(link.source) && filteredNodeIds.has(link.target)
    );

    // Update counts for display
    counts.all = allNodes.length;

    return { nodes: filteredNodes, links: filteredLinks, counts };
  };

  useEffect(() => {
    if (!svgRef.current || !results) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const container = svg.node().parentElement;
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    const { nodes, links, counts } = createNodesAndLinks(results, nodeFilter);
    setNodeCounts(counts);

    if (nodes.length === 0) return;

    // Enhanced zoom with better limits
    const zoom = d3.zoom()
      .scaleExtent([0.1, 5])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    const g = svg.append('g');

    // Enhanced force simulation for better layout with more nodes
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links).id(d => d.id).distance(80).strength(0.8))
      .force('charge', d3.forceManyBody().strength(-300).distanceMax(400))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(d => d.size + 20).strength(0.9))
      .force('radial', d3.forceRadial(200, width / 2, height / 2).strength(0.1));

    simulationRef.current = simulation;

    // Enhanced links with gradient colors
    const link = g.append('g')
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke', '#d1d5db')
      .attr('stroke-opacity', 0.8)
      .attr('stroke-width', 1.5)
      .style('stroke-dasharray', '3,3');

    // Enhanced nodes with better styling
    const node = g.append('g')
      .selectAll('circle')
      .data(nodes)
      .enter()
      .append('circle')
      .attr('r', d => d.size)
      .attr('fill', d => d.color)
      .attr('stroke', d => selectedNode?.id === d.id ? '#fbbf24' : 'rgba(255, 255, 255, 0.8)')
      .attr('stroke-width', d => selectedNode?.id === d.id ? 3 : 1.5)
      .style('cursor', 'pointer')
      .style('filter', 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))')
      .call(d3.drag()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }));

    // Enhanced labels with better positioning
    const labels = g.append('g')
      .selectAll('text')
      .data(nodes)
      .enter()
      .append('text')
      .text(d => d.label.length > 18 ? d.label.substring(0, 15) + '...' : d.label)
      .attr('font-size', '9px')
      .attr('fill', '#111827')
      .attr('text-anchor', 'middle')
      .attr('dy', d => d.size + 18)
      .style('pointer-events', 'none')
      .style('font-weight', '500')
      .style('text-shadow', '1px 1px 2px rgba(255,255,255,0.8)');

    // Enhanced node interactions
    node
      .on('click', (event, d) => {
        setSelectedNode(d);
        // Highlight connected nodes
        node.attr('stroke-width', n => n.id === d.id ? 3 : 1.5)
          .attr('stroke', n => n.id === d.id ? '#fbbf24' : 'rgba(255, 255, 255, 0.8)');
      })
      .on('mouseover', (event, d) => {
        const [x, y] = d3.pointer(event, svg.node());
        setTooltip({
          x: x + 10,
          y: y - 10,
          content: `${d.type.toUpperCase()}: ${d.label}`
        });

        // Highlight on hover
        d3.select(event.target)
          .attr('stroke', '#60a5fa')
          .attr('stroke-width', 2.5)
          .style('filter', 'drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3))');
      })
      .on('mouseout', (event, d) => {
        setTooltip(null);

        // Reset highlight unless selected
        if (selectedNode?.id !== d.id) {
          d3.select(event.target)
            .attr('stroke', 'rgba(255, 255, 255, 0.8)')
            .attr('stroke-width', 1.5)
            .style('filter', 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))');
        }
      });

    simulation.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      node
        .attr('cx', d => d.x)
        .attr('cy', d => d.y);

      labels
        .attr('x', d => d.x)
        .attr('y', d => d.y);
    });

    // Enhanced controls
    const zoomIn = () => {
      svg.transition().duration(250).call(zoom.scaleBy, 1.5);
    };

    const zoomOut = () => {
      svg.transition().duration(250).call(zoom.scaleBy, 1 / 1.5);
    };

    const resetView = () => {
      svg.transition().duration(400).call(
        zoom.transform,
        d3.zoomIdentity.translate(width / 2, height / 2).scale(1)
      );
    };

    svg.node().__zoomIn = zoomIn;
    svg.node().__zoomOut = zoomOut;
    svg.node().__resetView = resetView;

    return () => {
      simulation.stop();
    };
  }, [results, selectedNode, nodeFilter]);

  const handleZoomIn = () => {
    const svg = svgRef.current;
    if (svg && svg.__zoomIn) {
      svg.__zoomIn();
    }
  };

  const handleZoomOut = () => {
    const svg = svgRef.current;
    if (svg && svg.__zoomOut) {
      svg.__zoomOut();
    }
  };

  const handleResetView = () => {
    const svg = svgRef.current;
    if (svg && svg.__resetView) {
      svg.__resetView();
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getNodeIcon = (type) => {
    const icons = {
      domain: Globe,
      subdomain: Network,
      ip: MapPin,
      ssl: Lock,
      tech: Code,
      port: Server,
      dns: Database,
      whois: FileText
    };
    return icons[type] || Globe;
  };

  // Helper function to render complex data structures in a human-readable format
  const renderDataValue = (value, key = '') => {
    if (value === null || value === undefined) {
      return <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.6875rem' }}>null</span>;
    }

    if (typeof value === 'boolean') {
      return (
        <StatusBadge variant={value ? 'success' : 'error'}>
          {value ? 'Yes' : 'No'}
        </StatusBadge>
      );
    }

    if (typeof value === 'string') {
      // Handle dates
      if (key && (key.includes('date') || key.includes('time') || key.includes('created') || key.includes('updated'))) {
        if (value.match(/^\d{4}-\d{2}-\d{2}/) || value.includes('T')) {
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              return <span style={{ fontSize: '0.6875rem' }}>{date.toLocaleString()}</span>;
            }
          } catch (e) {
            // If date parsing fails, show original string
          }
        }
      }

      // Handle URLs
      if (value.startsWith('http://') || value.startsWith('https://')) {
        return (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#3b82f6',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              fontSize: '0.6875rem'
            }}
          >
            {value.length > 35 ? value.substring(0, 32) + '...' : value}
            <ExternalLink size={10} />
          </a>
        );
      }

      // Handle IP addresses
      if (value.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/)) {
        return <code style={{
          background: 'rgba(30, 64, 175, 0.05)',
          padding: '0.125rem 0.25rem',
          borderRadius: '0.25rem',
          fontFamily: 'monospace',
          fontSize: '0.6875rem',
          border: '1px solid rgba(30, 64, 175, 0.1)'
        }}>{value}</code>;
      }

      return <span style={{ fontSize: '0.75rem' }}>{value}</span>;
    }

    if (typeof value === 'number') {
      // Format large numbers
      if (value > 1000000) {
        return <span style={{ fontSize: '0.75rem' }}>{(value / 1000000).toFixed(2)}M</span>;
      } else if (value > 1000) {
        return <span style={{ fontSize: '0.75rem' }}>{(value / 1000).toFixed(1)}K</span>;
      }
      return <span style={{ fontSize: '0.75rem' }}>{value.toLocaleString()}</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.6875rem' }}>No items</span>;
      }

      return (
        <ArrayContainer>
          {value.map((item, index) => (
            <ArrayItem key={index}>
              <strong style={{ color: '#4b5563', fontSize: '0.6875rem' }}>#{index + 1}:</strong> {renderDataValue(item)}
            </ArrayItem>
          ))}
        </ArrayContainer>
      );
    }

    if (typeof value === 'object') {
      const entries = Object.entries(value);

      if (entries.length === 0) {
        return <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.6875rem' }}>Empty object</span>;
      }

      return (
        <NestedContainer>
          {entries.map(([objKey, objValue]) => (
            <KeyValuePair key={objKey}>
              <ObjectKey>{objKey.replace(/_/g, ' ')}</ObjectKey>
              <ObjectValue>{renderDataValue(objValue, objKey)}</ObjectValue>
            </KeyValuePair>
          ))}
        </NestedContainer>
      );
    }

    return <span style={{ fontSize: '0.75rem' }}>{String(value)}</span>;
  };

  const renderNodeDetails = () => {
    if (!selectedNode) {
      return (
        <EmptyDetailsState>
          <EmptyIcon>
            <Target size={24} />
          </EmptyIcon>
          <EmptyTitle>No Node Selected</EmptyTitle>
          <EmptyDescription>
            Click on a node in the visualization to view detailed information.
          </EmptyDescription>
        </EmptyDetailsState>
      );
    }

    const IconComponent = getNodeIcon(selectedNode.type);

    // For domain nodes, show comprehensive scan data organized by sections
    if (selectedNode.type === 'domain' && selectedNode.data) {
      const scanData = selectedNode.data;

      return (
        <NodeDetails>
          <NodeHeader>
            <NodeIcon color={selectedNode.color}>
              <IconComponent size={16} />
            </NodeIcon>
            <NodeTitle>
              <NodeName>{selectedNode.label}</NodeName>
              <NodeType>Domain Analysis</NodeType>
            </NodeTitle>
          </NodeHeader>

          {/* IP Details Section */}
          {scanData.ip_details && (
            <DataSection>
              <DataSectionTitle>
                <MapPin size={14} />
                IP Details
              </DataSectionTitle>
              <DataContainer>
                <DataGrid>
                  {Object.entries(scanData.ip_details)
                    .filter(([key]) => !EXCLUDED_FIELDS.has(key.toLowerCase()))
                    .map(([key, value]) => (
                      <DataRow key={key}>
                        <DataLabel>{key.replace(/_/g, ' ')}</DataLabel>
                        <DataValue>{renderDataValue(value, key)}</DataValue>
                      </DataRow>
                    ))}
                </DataGrid>
              </DataContainer>
            </DataSection>
          )}

          {/* Dig Output Section */}
          {scanData.dig_output && (
            <DataSection>
              <DataSectionTitle>
                <Database size={14} />
                Dig Output
              </DataSectionTitle>
              <DataContainer>
                <DataGrid>
                  {Object.entries(scanData.dig_output)
                    .filter(([key]) => !EXCLUDED_FIELDS.has(key.toLowerCase()))
                    .map(([key, value]) => (
                      <DataRow key={key}>
                        <DataLabel>{key.replace(/_/g, ' ')}</DataLabel>
                        <DataValue>{renderDataValue(value, key)}</DataValue>
                      </DataRow>
                    ))}
                </DataGrid>
              </DataContainer>
            </DataSection>
          )}

          {/* Open Ports Section (excluding failed ports) */}
          {scanData.open_ports && (
            <DataSection>
              <DataSectionTitle>
                <Server size={14} />
                Open Ports
              </DataSectionTitle>
              <DataContainer>
                <DataGrid>
                  {scanData.open_ports.openPorts && (
                    <DataRow>
                      <DataLabel>Open Ports</DataLabel>
                      <DataValue>{renderDataValue(scanData.open_ports.openPorts)}</DataValue>
                    </DataRow>
                  )}
                </DataGrid>
              </DataContainer>
            </DataSection>
          )}

          {/* Response Headers Section */}
          {scanData.response_headers && (
            <DataSection>
              <DataSectionTitle>
                <FileText size={14} />
                Response Headers
              </DataSectionTitle>
              <DataContainer>
                <DataGrid>
                  {Object.entries(scanData.response_headers)
                    .filter(([key]) => !EXCLUDED_FIELDS.has(key.toLowerCase()) && key && scanData.response_headers[key])
                    .map(([key, value]) => (
                      <DataRow key={key}>
                        <DataLabel>{key.replace(/_/g, ' ')}</DataLabel>
                        <DataValue>{renderDataValue(value, key)}</DataValue>
                      </DataRow>
                    ))}
                </DataGrid>
              </DataContainer>
            </DataSection>
          )}

          {/* Security Headers Section */}
          {scanData.security_headers && (
            <DataSection>
              <DataSectionTitle>
                <Shield size={14} />
                Security Headers
              </DataSectionTitle>
              <DataContainer>
                <DataGrid>
                  {/* Access the nested security_headers object */}
                  {Object.entries(scanData.security_headers.security_headers || {})
                    .filter(([key]) => !EXCLUDED_FIELDS.has(key.toLowerCase()))
                    .map(([key, value]) => (
                      <DataRow key={key}>
                        <DataLabel>{key.replace(/_/g, ' ')}</DataLabel>
                        <DataValue>{renderDataValue(value, key)}</DataValue>
                      </DataRow>
                    ))}
                </DataGrid>
              </DataContainer>
            </DataSection>
          )}

          {/* SSL Details Section */}
          {scanData.ssl_details && (
            <DataSection>
              <DataSectionTitle>
                <Lock size={14} />
                SSL Details
              </DataSectionTitle>
              <DataContainer>
                <DataGrid>
                  {Object.entries(scanData.ssl_details)
                    .filter(([key]) => !EXCLUDED_FIELDS.has(key.toLowerCase()))
                    .map(([key, value]) => (
                      <DataRow key={key}>
                        <DataLabel>{key.replace(/_/g, ' ')}</DataLabel>
                        <DataValue>{renderDataValue(value, key)}</DataValue>
                      </DataRow>
                    ))}
                </DataGrid>
              </DataContainer>
            </DataSection>
          )}

          {/* Subdomains Section */}
          {scanData.subdomains && (
            <DataSection>
              <DataSectionTitle>
                <Network size={14} />
                Subdomains
              </DataSectionTitle>
              <DataContainer>
                <DataGrid>
                  {Object.entries(scanData.subdomains)
                    .filter(([key]) => !EXCLUDED_FIELDS.has(key.toLowerCase()))
                    .map(([key, value]) => (
                      <DataRow key={key}>
                        <DataLabel>{key.replace(/_/g, ' ')}</DataLabel>
                        <DataValue>{renderDataValue(value, key)}</DataValue>
                      </DataRow>
                    ))}
                </DataGrid>
              </DataContainer>
            </DataSection>
          )}

          {/* Tech Stack Section */}
          {scanData.tech_stack && (
            <DataSection>
              <DataSectionTitle>
                <Code size={14} />
                Tech Stack
              </DataSectionTitle>
              <DataContainer>
                <DataGrid>
                  {/* Technologies */}
                  <DataRow>
                    <DataLabel colSpan="2" style={{ fontWeight: '600', textAlign: 'center', fontSize: '0.75rem' }}>
                      Technologies
                    </DataLabel>
                  </DataRow>
                  {Object.values(scanData.tech_stack)
                    .flatMap(domainData => domainData.tech || [])
                    .map((tech, index) => (
                      <DataRow key={`tech-${index}`}>
                        <DataLabel>{tech.name}</DataLabel>
                        <DataValue>{tech.version || 'No version specified'}</DataValue>
                      </DataRow>
                    ))}

                  {/* Headers */}
                  <DataRow>
                    <DataLabel colSpan="2" style={{ fontWeight: '600', textAlign: 'center', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                      Headers
                    </DataLabel>
                  </DataRow>
                  {Object.values(scanData.tech_stack)
                    .flatMap(domainData => domainData.headers || [])
                    .map((header, index) => (
                      <DataRow key={`header-${index}`}>
                        <DataLabel>{header.name}</DataLabel>
                        <DataValue>{header.value}</DataValue>
                      </DataRow>
                    ))}
                </DataGrid>
              </DataContainer>
            </DataSection>
          )}

          {/* WHOIS Output Section */}
          {scanData.whois_output && (
            <DataSection>
              <DataSectionTitle>
                <FileText size={14} />
                WHOIS Output
              </DataSectionTitle>
              <DataContainer>
                <DataGrid>
                  {Object.entries(scanData.whois_output)
                    .filter(([key]) => !EXCLUDED_FIELDS.has(key.toLowerCase()))
                    .map(([key, value]) => (
                      <DataRow key={key}>
                        <DataLabel>{key.replace(/_/g, ' ')}</DataLabel>
                        <DataValue>{renderDataValue(value, key)}</DataValue>
                      </DataRow>
                    ))}
                </DataGrid>
              </DataContainer>
            </DataSection>
          )}
        </NodeDetails>
      );
    }

    // For other node types, show basic information
    return (
      <NodeDetails>
        <NodeHeader>
          <NodeIcon color={selectedNode.color}>
            <IconComponent size={16} />
          </NodeIcon>
          <NodeTitle>
            <NodeName>{selectedNode.label}</NodeName>
            <NodeType>{selectedNode.type}</NodeType>
          </NodeTitle>
        </NodeHeader>

        <DataSection>
          <DataSectionTitle>
            <Info size={14} />
            Basic Information
          </DataSectionTitle>
          <DataContainer>
            <DataGrid>
              <DataRow>
                <DataLabel>Type</DataLabel>
                <DataValue>
                  <StatusBadge variant="info">{selectedNode.type}</StatusBadge>
                </DataValue>
              </DataRow>
              <DataRow>
                <DataLabel>Label</DataLabel>
                <DataValue>{selectedNode.label}</DataValue>
              </DataRow>
            </DataGrid>
          </DataContainer>
        </DataSection>

        {selectedNode.data && Object.keys(selectedNode.data).length > 0 && (
          <DataSection>
            <DataSectionTitle>
              <Hash size={14} />
              Detailed Data
            </DataSectionTitle>
            <DataContainer>
              <DataGrid>
                {Object.entries(selectedNode.data)
                  .filter(([key]) => !EXCLUDED_FIELDS.has(key.toLowerCase()))
                  .map(([key, value]) => (
                    <DataRow key={key}>
                      <DataLabel>{key.replace(/_/g, ' ')}</DataLabel>
                      <DataValue>
                        {renderDataValue(value, key)}
                      </DataValue>
                    </DataRow>
                  ))}
              </DataGrid>
            </DataContainer>
          </DataSection>
        )}
      </NodeDetails>
    );
  };

  return (
    <>
      <GlobalStyle />
      <PageContainer>
        <MainWrapper>
          <SearchSection>
            <SearchForm onSubmit={handleSubmit}>
              <SearchInputWrapper>
                <SearchIcon>
                  <Search size={16} />
                </SearchIcon>
                <SearchInput
                  type="text"
                  placeholder="Enter target domain (e.g., example.com)"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  disabled={isScanning}
                />
              </SearchInputWrapper>

              {isScanning && (
                <ProgressBarWrapper>
                  <ProgressBarContainer>
                    <ProgressBar width={progress} />
                  </ProgressBarContainer>
                  <ProgressText>{Math.floor(progress)}%</ProgressText>
                </ProgressBarWrapper>
              )}

              <SearchButton type="submit" disabled={isScanning}>
                {isScanning ? (
                  <>
                    <Activity size={16} />
                    Scanning
                  </>
                ) : (
                  <>
                    <Shield size={16} />
                    Scan
                  </>
                )}
              </SearchButton>
            </SearchForm>
          </SearchSection>

          {error && (
            <ErrorSection>
              <ErrorMessage>
                <AlertTriangle size={12} />
                {error}
              </ErrorMessage>
            </ErrorSection>
          )}

          <MainContentLayout>
            <ScansListSection>
              <ScansHeader>
                <Database size={16} />
                <ScansTitle>Scan History</ScansTitle>
              </ScansHeader>
              {loadingScans ? (
                <div style={{ padding: '1.25rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.8125rem' }}>
                  Loading scans...
                </div>
              ) : scans.length > 0 ? (
                <ScansList>
                  {scans.map((scan) => (
                    <ScanItem
                      key={scan.id}
                      onClick={() => handleScanClick(scan.id)}
                      active={activeScanId === scan.id}
                    >
                      <ScanItemHeader>
                        <ScanDomain active={activeScanId === scan.id}>
                          {activeScanId === scan.id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                          {scan.domain}
                        </ScanDomain>
                        <ScanDate>
                          <Clock size={10} />
                          {formatDate(scan.created_at)}
                        </ScanDate>
                      </ScanItemHeader>
                      <ScanId>ID: {scan.id}</ScanId>
                    </ScanItem>
                  ))}
                </ScansList>
              ) : (
                <div style={{ padding: '1.25rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.8125rem' }}>
                  No scans available. Run a scan to see results.
                </div>
              )}
            </ScansListSection>

            <VisualizationSection>
              <VisualizationHeader>
                <VisualizationHeaderLeft>
                  <Layers size={16} />
                  <VisualizationTitle>Network Visualization</VisualizationTitle>
                </VisualizationHeaderLeft>
                <VisualizationHeaderRight>
                  {results && (
                    <>
                      <NodeCount>
                        {nodeCounts[nodeFilter] || 0} nodes
                      </NodeCount>
                      <FilterControls>
                        <FilterButton
                          active={nodeFilter === 'all'}
                          onClick={() => setNodeFilter('all')}
                        >
                          <Filter size={10} />
                          All
                        </FilterButton>
                        {Object.entries(NODE_COLORS).map(([type]) => (
                          nodeCounts[type] > 0 && (
                            <FilterButton
                              key={type}
                              active={nodeFilter === type}
                              onClick={() => setNodeFilter(type)}
                            >
                              {type} ({nodeCounts[type]})
                            </FilterButton>
                          )
                        ))}
                      </FilterControls>
                    </>
                  )}
                </VisualizationHeaderRight>
              </VisualizationHeader>
              <VisualizationContent>
                {results ? (
                  <VisualizationContainer>
                    <SVGContainer ref={svgRef} />

                    <Controls>
                      <ControlButton onClick={handleZoomIn} title="Zoom In">
                        <ZoomIn size={12} />
                      </ControlButton>
                      <ControlButton onClick={handleZoomOut} title="Zoom Out">
                        <ZoomOut size={12} />
                      </ControlButton>
                      <ControlButton onClick={handleResetView} title="Reset View">
                        <Maximize2 size={12} />
                      </ControlButton>
                    </Controls>

                    <Legend>
                      <LegendTitle>
                        <Info size={10} />
                        Node Types
                      </LegendTitle>
                      {Object.entries(NODE_COLORS).map(([type, color]) => (
                        <LegendItem key={type}>
                          <LegendDot color={color} />
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                          <LegendCount>({nodeCounts[type] || 0})</LegendCount>
                        </LegendItem>
                      ))}
                    </Legend>

                    {tooltip && (
                      <Tooltip style={{ left: tooltip.x, top: tooltip.y }}>
                        {tooltip.content}
                      </Tooltip>
                    )}
                  </VisualizationContainer>
                ) : (
                  <EmptyVisualizationState>
                    <EmptyIcon>
                      <Network size={28} />
                    </EmptyIcon>
                    <EmptyTitle>No Data to Visualize</EmptyTitle>
                    <EmptyDescription>
                      Select a scan from the history or run a new scan to see the interactive network visualization.
                    </EmptyDescription>
                  </EmptyVisualizationState>
                )}
              </VisualizationContent>
            </VisualizationSection>

            <DetailsPanel>
              <DetailsPanelHeader>
                <Target size={16} />
                <DetailsPanelTitle>Node Details</DetailsPanelTitle>
              </DetailsPanelHeader>
              <DetailsPanelContent>
                {renderNodeDetails()}
              </DetailsPanelContent>
            </DetailsPanel>
          </MainContentLayout>
        </MainWrapper>
      </PageContainer>
    </>
  );
}